import React, { useState, useRef, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './ComponentInspector.css';
import { getInspector, customInspectors } from './inspectors';
// eslint-disable-next-line no-unused-vars
import languagesLib from '@cospired/i18n-iso-languages';
// eslint-disable-next-line no-unused-vars
import emojiFlags from 'emoji-flags';
import ReactCountryFlag from 'react-country-flag';
import ReactDOM from 'react-dom';
import IconPicker from '../iconPicker/IconPicker';
import PhotoPicker from '../photoPicker/PhotoPicker';
import { useNavigate, useParams } from 'react-router-dom';

function SortableComponent({ 
  func, 
  index, 
  expandedComponents, 
  onToggle, 
  onDelete, 
  CustomInspector, 
  handleCustomUpdate, 
  renderDefaultInspector,
  isIconPickerOpen,
  setIsIconPickerOpen,
  iconPickerPosition,
  setIconPickerPosition,
  setActiveIconPreview,
  isPhotoPickerOpen,
  setIsPhotoPickerOpen,
  photoPickerPosition,
  setPhotoPickerPosition,
  setActivePhotoPreview,
  bringToFront
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `sortable-${index}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const formatComponentName = (name) => {
    return name
      .replace(/([A-Z])/g, ' $1')
      .trim()
      .replace(/\s+([A-Z]{2,})/g, ' $1');
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`component-box ${isDragging ? 'dragging' : ''}`}
    >
      <div className="component-name" onClick={() => onToggle(index)}>
        <div className="drag-handle" {...attributes} {...listeners}>
          <span>⋮⋮</span>
        </div>
        <span className="component-index">{index + 1}</span>
        <span>{formatComponentName(func.name)}</span>
        <div className="component-buttons" onClick={(e) => e.stopPropagation()}>
          <button 
            className={`collapse-button ${expandedComponents[index] ? 'expanded' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              onToggle(index);
            }}
          >
            <svg 
              width="12" 
              height="12" 
              viewBox="0 0 12 12" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
            >
              <path 
                d="M2 4L6 8L10 4" 
                stroke="white" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <button 
            className="delete-button-custom"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(index);
            }}
            title="Delete component"
          >
            ×
          </button>
        </div>
      </div>
      <div className={`props-container ${expandedComponents[index] ? 'expanded' : ''}`}>
        {CustomInspector ? (
          <CustomInspector 
            func={func} 
            index={index} 
            onUpdate={handleCustomUpdate}
            isIconPickerOpen={isIconPickerOpen}
            setIsIconPickerOpen={setIsIconPickerOpen}
            iconPickerPosition={iconPickerPosition}
            setIconPickerPosition={setIconPickerPosition}
            bringToFront={bringToFront}
            setActiveIconPreview={setActiveIconPreview}
            isPhotoPickerOpen={isPhotoPickerOpen}
            setIsPhotoPickerOpen={setIsPhotoPickerOpen}
            photoPickerPosition={photoPickerPosition}
            setPhotoPickerPosition={setPhotoPickerPosition}
            setActivePhotoPreview={setActivePhotoPreview}
          />
        ) : (
          renderDefaultInspector(func, index, {
            isIconPickerOpen,
            setIsIconPickerOpen,
            iconPickerPosition,
            setIconPickerPosition,
            setActiveIconPreview,
            isPhotoPickerOpen,
            setIsPhotoPickerOpen,
            photoPickerPosition,
            setPhotoPickerPosition,
            setActivePhotoPreview
          })
        )}
      </div>
    </div>
  );
}

const ComponentInspector = ({ template, isVisible = true, onUpdate, currentLang, setCurrentLang, availableLanguages, multiLanguage, selectedLanguages, onLanguagesChange, unbindLanguageTemplates, onMultiLanguageToggle }) => {
  const { id } = useParams();
  // State for managing modal z-indices
  const [modalZIndices, setModalZIndices] = useState({
    properties: 100000,
    iconPicker: 100000,
    photoPicker: 100000
  });

  // Function to bring a modal to front
  const bringToFront = (modalId) => {
    setModalZIndices(prev => {
      const highest = Math.max(...Object.values(prev));
      return {
        ...prev,
        [modalId]: highest + 1
      };
    });
  };

  // Shared icon picker state
  const [isIconPickerOpen, setIsIconPickerOpen] = useState(false);
  const [isPhotoPickerOpen, setIsPhotoPickerOpen] = useState(false);
  // Calculate initial position on the right side
  const modalWidth = 500;
  const rightGap = 140; // Increased gap from the right edge
  const modalHeight = 600;
  const initialPosition = {
    x: window.innerWidth - modalWidth - rightGap,
    y: (window.innerHeight - modalHeight) / 2 // centered vertically
  };
  const [iconPickerPosition, setIconPickerPosition] = useState(initialPosition);
  const [photoPickerPosition, setPhotoPickerPosition] = useState(initialPosition);
  const [activeIconPreview, setActiveIconPreview] = useState(null);
  const [activePhotoPreview, setActivePhotoPreview] = useState(null);

  // Log position changes
  useEffect(() => {
    console.log('Icon picker position updated:', iconPickerPosition);
  }, [iconPickerPosition]);

  // eslint-disable-next-line no-unused-vars
  const navigate = useNavigate();
  const [expandedComponents, setExpandedComponents] = useState({});
  const [isAddModuleExpanded, setIsAddModuleExpanded] = useState(false);
  const [isPropertiesOpen, setIsPropertiesOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [expandedSections, setExpandedSections] = useState({ themeColors: true });
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isLanguagesCollapsed, setIsLanguagesCollapsed] = useState(true);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [dropdownAnchor, setDropdownAnchor] = useState(null);
  const [languageSearch, setLanguageSearch] = useState('');
  const modulePickerRef = useRef(null);
  const inspectorContentRef = useRef(null);
  const addLanguageBtnRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const [showFlagDropdown, setShowFlagDropdown] = useState(false);
  const [flagDropdownPos, setFlagDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const flagBtnRef = useRef(null);
  const languageDropdownRef = useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const applyThemeColors = (colors) => {
    const root = document.documentElement;
    Object.entries(colors).forEach(([key, value]) => {
      root.style.setProperty(`--${key}-color`, value);
    });
  };

  useEffect(() => {
    if (template?.colors) {
      applyThemeColors(template.colors);
    }
  }, [template?.colors]);

  const handleColorsUpdate = (newColors) => {
    applyThemeColors(newColors);
    onUpdate({
      ...template,
      colors: newColors
    });
  };

  const handleMouseDown = (e) => {
    if (e.target.closest('.properties-header')) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging, dragStart]);

  useEffect(() => {
    if (!showLanguageDropdown) return;
    function handleClickOutside(event) {
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target) && addLanguageBtnRef.current && !addLanguageBtnRef.current.contains(event.target)) {
        setShowLanguageDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLanguageDropdown]);

  // Add a shallow equality check for module name order
  function shallowModuleOrderEqual(arr1, arr2) {
    if (arr1.length !== arr2.length) return false;
    for (let i = 0; i < arr1.length; i++) {
      if (arr1[i]?.name !== arr2[i]?.name) return false;
    }
    return true;
  }

  // Check if all selected language contexts have the same module order/names
  const allContextsEqual = (() => {
    if (!selectedLanguages || selectedLanguages.length < 2) return true;
    const first = template.functions[selectedLanguages[0]] || [];
    return selectedLanguages.every(code => shallowModuleOrderEqual(first, template.functions[code] || []));
  })();

  const unbind = typeof unbindLanguageTemplates !== 'undefined' ? unbindLanguageTemplates : template?.unbindLanguageTemplates || false;

  // --- New logic: force unbindLanguageTemplates true if 2+ languages and not allContextsEqual ---
  useEffect(() => {
    if (
      multiLanguage &&
      (selectedLanguages || []).length > 1 &&
      !allContextsEqual &&
      !unbind
    ) {
      onUpdate({
        ...template,
        unbindLanguageTemplates: true
      });
    }
    // Only set to true, never auto-false (user can turn it off if templates match)
    // eslint-disable-next-line
  }, [multiLanguage, selectedLanguages, allContextsEqual]);

  if (!isVisible) {
    return null;
  }

  // Default colors if none are provided
  const defaultColors = {
    primary: "#000000",
    secondary: "#666666",
    background: "#ffffff",
    highlight: "#ff4444",
    border: "#dddddd",
    icon: "#f6b5b3"
  };

  const colors = template?.colors || defaultColors;

  // Curated list of 23 common languages with their most associated country flag
  const curatedLanguages = [
    { code: 'en', name: 'English', countryCode: 'GB' },
    { code: 'de', name: 'German', countryCode: 'DE' },
    { code: 'fr', name: 'French', countryCode: 'FR' },
    { code: 'es', name: 'Spanish', countryCode: 'ES' },
    { code: 'it', name: 'Italian', countryCode: 'IT' },
    { code: 'nl', name: 'Dutch', countryCode: 'NL' },
    { code: 'pl', name: 'Polish', countryCode: 'PL' },
    { code: 'pt', name: 'Portuguese', countryCode: 'PT' },
    { code: 'ru', name: 'Russian', countryCode: 'RU' },
    { code: 'uk', name: 'Ukrainian', countryCode: 'UA' },
    { code: 'el', name: 'Greek', countryCode: 'GR' },
    { code: 'sv', name: 'Swedish', countryCode: 'SE' },
    { code: 'cs', name: 'Czech', countryCode: 'CZ' },
    { code: 'hu', name: 'Hungarian', countryCode: 'HU' },
    { code: 'ro', name: 'Romanian', countryCode: 'RO' },
    { code: 'da', name: 'Danish', countryCode: 'DK' },
    { code: 'fi', name: 'Finnish', countryCode: 'FI' },
    { code: 'no', name: 'Norwegian', countryCode: 'NO' },
    { code: 'ar', name: 'Arabic', countryCode: 'SA' },
    { code: 'zh', name: 'Chinese (Simplified)', countryCode: 'CN' },
    { code: 'ja', name: 'Japanese', countryCode: 'JP' },
    { code: 'tr', name: 'Turkish', countryCode: 'TR' },
    { code: 'he', name: 'Hebrew', countryCode: 'IL' },
  ];

  // Helper to render flag or globe (always flag for curated list)
  // eslint-disable-next-line no-unused-vars
  const renderFlag = (countryCode, size = '1.2em') => (
    <ReactCountryFlag
      countryCode={countryCode}
      svg
      style={{ width: size, height: size, borderRadius: '50%', marginRight: 8, verticalAlign: 'middle' }}
      title={countryCode}
    />
  );

  // Filtered languages for search
  const filteredLanguages = curatedLanguages.filter(l => {
    // Filter out already selected languages
    if ((selectedLanguages || []).includes(l.code)) return false;
    return (
      l.name.toLowerCase().includes(languageSearch.toLowerCase()) ||
      (new Intl.DisplayNames(['en'], { type: 'region' }).of(l.countryCode) || '').toLowerCase().includes(languageSearch.toLowerCase())
    );
  });

  const primaryLanguage = template?.primaryLanguage || null;
  // Find selected language object
  // eslint-disable-next-line no-unused-vars
  const selectedLang = curatedLanguages.find(l => l.code === primaryLanguage);

  const languages = template?.languages || [];

  // eslint-disable-next-line no-unused-vars
  const handleMultiLanguageToggle = () => {
    onUpdate({
      ...template,
      multiLanguage: !multiLanguage,
      languages: !multiLanguage ? [] : template.languages || [],
      // Optionally reset unbindLanguageTemplates if disabling multiLanguage
      unbindLanguageTemplates: !multiLanguage ? false : template.unbindLanguageTemplates || false
    });
  };

  const handleUnbindLanguageTemplatesToggle = () => {
    onUpdate({
      ...template,
      unbindLanguageTemplates: !unbind
    });
  };

  // eslint-disable-next-line no-unused-vars
  const handleAddLanguage = (langCode) => {
    if (!languages.includes(langCode)) {
      onUpdate({
        ...template,
        languages: [...languages, langCode]
      });
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleRemoveLanguage = (langCode) => {
    onUpdate({
      ...template,
      languages: languages.filter(l => l !== langCode)
    });
  };

  // Map language code to country code for flag (only for common languages)
  const langToCountry = {
    en: 'GB', es: 'ES', pt: 'PT', fr: 'FR', de: 'DE', it: 'IT', ru: 'RU', zh: 'CN', ja: 'JP', ko: 'KR', ar: 'SA', hi: 'IN', tr: 'TR', nl: 'NL', sv: 'SE', pl: 'PL', el: 'GR', he: 'IL', th: 'TH', id: 'ID',
    // ...add more as needed
  };

  // Helper to get country name from country code
  // eslint-disable-next-line no-unused-vars
  const getCountryName = (countryCode) => {
    try {
      return new Intl.DisplayNames(['en'], { type: 'region' }).of(countryCode);
    } catch {
      return countryCode;
    }
  };

  // Helper to render flag or globe
  // eslint-disable-next-line no-unused-vars
  const renderFlagOrGlobe = (langCode, size = '1.2em') => {
    const country = langToCountry[langCode];
    if (country) {
      return (
        <ReactCountryFlag
          countryCode={country}
          svg
          style={{ width: size, height: size, borderRadius: '50%', marginRight: 8, verticalAlign: 'middle' }}
          title={langCode}
        />
      );
    } else {
      return <span style={{ fontSize: size, marginRight: 8, verticalAlign: 'middle' }}>🌐</span>;
    }
  };

  // Get available modules from the inspectors index
  const availableModules = Object.keys(customInspectors);

  // Format component name to be more readable
  const formatComponentName = (name) => {
    // Add space before capital letters and trim
    return name
      .replace(/([A-Z])/g, ' $1')
      .trim()
      // Handle special cases like "RSVP" or "UI"
      .replace(/\s+([A-Z]{2,})/g, ' $1');
  };

  const getDefaultProps = (moduleType) => {
    switch (moduleType) {
      case 'WelcomeImage':
        return {
          backgroundImage: 'https://yjpipxmxaqnzbxyynuit.supabase.co/storage/v1/object/public/EventInvites/GreekVitesExample/hike.png',
          name1: 'Maria',
          name2: 'Alex',
          plusText: '&'
        };
      case 'EventCalendar':
        return {
          date: new Date(Date.now() + 6 * 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 6 months from now
          names: [],
          headerText: 'Dear family and friends,',
          topText: 'With love in our hearts, we invite you to celebrate the beginning of our life together.',
          bottomText: 'We cannot imagine this day without you!',
          calendar: {
            countryLocale: 'en-US',
            days: {
              Mon: 'Mon',
              Tue: 'Tue',
              Wed: 'Wed',
              Thu: 'Thu',
              Fri: 'Fri',
              Sat: 'Sat',
              Sun: 'Sun'
            }
          }
        };
      case 'MediaAsset':
        return {
          src: 'https://yjpipxmxaqnzbxyynuit.supabase.co/storage/v1/object/public/EventInvites/GreekVitesExample/cityWalk.png',
          className: 'engagement-photo',
          alt: 'Add your media content here',
          heightPercent: 100,
          shiftPercent: 0
        };
      case 'ContentSection':
        return {
          sectionTitle: 'Schedule of Events',
          scheduleItems: [
            {
              time: '16:00',
              event: 'Welcome Reception'
            },
            {
              time: '17:00',
              event: 'Ceremony Begins'
            },
            {
              time: '18:00',
              event: 'Cocktail Hour'
            },
            {
              time: '19:00',
              event: 'Dinner & Celebrations'
            }
          ]
        };
      case 'MessageSection':
        return {
          header: "Information about transport and accommodation",
          paragraphs: [
            "We want you to enjoy our day to the fullest and without worries, so we have arranged accommodation for you near the wedding venue. We have double and quadruple rooms available in two guesthouses. If you don't mind sharing a room with four people, please let us know – it will help us allocate the accommodation better.",
            "Capacity is limited, so please confirm as soon as possible. Check-in will be possible from 13:00, and we will arrange transport between the guesthouses and the wedding venue for you – details will be specified.",
            "We look forward to seeing you! ❤️"
          ],
          icon: {
            viewBox: "0 0 24 24",
            svg_path: "M12 1l-12 12h3v10h18v-10h3l-12-12zm0 18c-1.607-1.626-3-2.84-3-4.027 0-1.721 2.427-2.166 3-.473.574-1.695 3-1.246 3 .473 0 1.187-1.393 2.402-3 4.027zm8-11.907l-3-3v-2.093h3v5.093z"
          }
        };
      case 'LocationSection':
        return {
          locations: [
            {
              title: 'Saint Isidore',
              city: 'Chapel of Sykiada',
              venue: '',
              time: '16:00 PM',
              mapUrl: 'https://maps.app.goo.gl/xxn39v9rT2GYULps8',
              icon: {
                svg_path: 'M21 24h-18v-4.144l4-3.536v-3.9l5-4.42 5 4.42v3.904l4 3.536v4.14zm-8-1v-3c0-.552-.448-1-1-1s-1 .448-1 1v3h2zm2-1h4v-1.239l-4-3.535v-3.905l-3-2.652-3 2.652v3.901l-4 3.535v1.243h4v-2c0-1.656 1.344-3 3-3s3 1.344 3 3v2zm3-8.849l6 5.356-1.328 1.493-4.672-4.184v-2.665zm-12 0l-6 5.356 1.328 1.493 4.672-4.184v-2.665zm7 2.849h-2v-2c0-.552.448-1 1-1s1 .448 1 1v2zm0-14h2v2h-2v.751l7 6.249-1.328 1.493-6.672-5.975-6.672 5.975-1.328-1.493 7-6.22v-.78h-2v-2h2v-2h2v2z',
                viewBox: '0 0 24 24',
                mapButtonText: 'Take me to the Church'
              }
            },
            {
              title: 'Villa del Balbianello',
              city: 'Dinner & Party Venue',
              venue: '',
              time: '18:00 PM',
              mapUrl: 'https://maps.app.goo.gl/AKjGn11utDB6VqqZ9',
              icon: {
                svg_path: 'M19.205 17.646c-.865.18-1.771.07-2.573-.323-.953-.466-1.682-1.291-2.026-2.295-.791-2.305-1.405-5.566-1.975-8.837l5.675-1.947c1.525 2.958 3.087 5.915 3.867 8.188.344 1.003.276 2.102-.19 3.055-.392.802-1.039 1.446-1.832 1.834l1.215 3.542c.29.814 1.322.926 2.392.809l.242.705-4.729 1.623-.261-.759c.903-.478 1.688-1.214 1.41-2.053l-1.215-3.542zm-15.356-.325c-.793-.388-1.44-1.032-1.832-1.834-.466-.953-.534-2.052-.19-3.055.78-2.273 2.342-5.23 3.867-8.188l5.675 1.947c-.57 3.271-1.184 6.532-1.975 8.837-.344 1.004-1.073 1.829-2.026 2.295-.802.393-1.708.503-2.573.323l-1.205 3.512c-.278.839.507 1.575 1.41 2.053l-.271.789-4.729-1.623.252-.735c1.07.118 2.102.005 2.392-.808l1.205-3.513zm14.901-2.821c.414 0 .75.336.75.75s-.336.75-.75.75-.75-.336-.75-.75.336-.75.75-.75zm-15-1c.414 0 .75.336.75.75s-.336.75-.75.75-.75-.336-.75-.75.336-.75.75-.75zm2.5-1.5c.414 0 .75.336.75.75s-.336.75-.75.75-.75-.336-.75-.75.336-.75.75-.75zm11.75-.107c.414 0 .75.336.75.75s-.336.75-.75.75-.75-.336-.75-.75.336-.75.75-.75zm1.939-1.893c-.663-1.525-1.441-3.012-2.178-4.512l-3.917 1.344c.214 1.059.41 2.121.636 3.168h5.459zm-10.419 0c.226-1.047.422-2.109.636-3.168l-3.917-1.344c-.737 1.5-1.515 2.987-2.178 4.512h5.459zm4.915-6.015l1.642-2.79c.141-.238.061-.545-.177-.685-.238-.139-.544-.062-.684.177l-1.643 2.79c-.14.238-.06.545.177.685.08.047.167.069.254.069.171 0 .338-.088.431-.246zm-4.552.246c-.171 0-.337-.088-.431-.246l-1.643-2.79c-.14-.238-.061-.545.177-.685s.544-.061.685.177l1.643 2.79c.14.238.061.545-.177.685-.08.047-.167.069-.254.069zm2.621-.708v-3.023c0-.277-.224-.5-.5-.5s-.5.223-.5.5v3.023c0 .276.224.5.5.5s.5-.224.5-.5z',
                viewBox: '0 0 24 24',
                mapButtonText: 'Take me to the Venue'
              }
            }
          ]
        };
      case 'DressCodeSection':
        return {
            topMessageProps: {
              icon: {
                viewBox: "0 0 24 24",
                svg_path: "M15.968 11l6.032 11.463s-2.112.804-5.554 1.249l-1.446-3.712.432 3.828c-1.062.105-2.228.172-3.479.172-1.219 0-2.352-.063-3.385-.164l.432-3.836-1.45 3.721c-3.458-.443-5.55-1.258-5.55-1.258l6.128-11.463h7.84zm-7.823-1l-1.162-4.09-2.44.794-1.543-2.429 5-4.275s2.039 1 4 1c1.961 0 3.978-1 3.978-1l5.022 4.275-1.543 2.429-2.44-.794-1.076 4.09h-7.796zm.362-8.709s1.497.709 3.493.709c1.996 0 3.546-.707 3.546-.707s-1.028 2.707-3.546 2.707-3.493-2.709-3.493-2.709z"
              },
              header: "DRESS CODE",
              paragraphs: [
                "We are very much looking forward to our unforgettable day!\nPlease support us with your smiles and hugs, as well as beautiful outfits matching the colors of our celebration."
              ],
              altBackground: false
            },
            colorProp: {
              svg: {
                viewBox: "0 0 64 64",
                d: "M 32 2 A 30 30 0 1 1 32 62 A 30 30 0 1 1 32 2 Z",
                border: "true"
              },
              colors: [
                "#FFF5CC",
                "#F6B5B3",
                "#ADD4A6",
                "#CDE9F5",
                "#C7CEEA"
              ]
            },
            bottomMessageProps: {
              paragraphs: [
                "We kindly ask gentlemen to choose a suit in a color other than black. For ladies, we recommend shoes with a wider heel, as the wedding will take place on a farm, and dresses that are longer rather than too short."
              ],
              altBackground: false
            }
        };
        case 'SpotifySection':
          return {
            messageSection: {
              "header": "Help us with our Wedding Playlist!",
              "paragraphs": [
                "We'd love your song suggestions to help make our celebration even more special. While we may not be able to include every track, your ideas will inspire the vibe and soundtrack of our big day. Thank you for sharing your favorites!"
              ],
              "forceColor": true
            }
          };
        case 'AttendanceQuestions':
          let id1 = crypto.randomUUID();
          let id2 = crypto.randomUUID();
          return {
            header: 'Will You Join Us?',
            messageProps: {
              header: 'RSVP',
              paragraphs: [
                "We would appreciate it if you could confirm your attendance here. If you are unable to attend, please let us know through the form as well.\nPlease fill out the form for your partner too, so we can better plan accommodation and the menu for the reception. ❤️"
              ],
              icon: {
                svg_path: 'M.026 24l11.974-11.607 11.974 11.607h-23.948zm11.964-23.961l-11.99 8.725v12.476l7.352-7.127-5.653-4.113 10.291-7.488 10.309 7.488-5.655 4.108 7.356 7.132v-12.476l-12.01-8.725z',
                viewBox: '0 0 24 24',
              },
              altBackground: false
            },
            questions: [
              {
                id: id1,
                type: 'open',
                headerQuestion: 'Could you please fill in your name?',
                placeholder: "",
                rows: 1
              },
              {
                id: id2,
                type: 'question',
                headerQuestion: 'Will you be attending our wedding?',
                options: ['Yes', 'No'],
                dependencies: [
                  {
                    questionId: id1,
                    expectedLength: 1
                  }
                ]
              }
            ]
          };
      case 'CountdownContainer':
        return {
          targetDate: new Date(Date.now() + 3 * 30 * 24 * 60 * 60 * 1000).toISOString(), // 3 months from now
          countdownText: 'Til our big day!',
          countdownLabels: {
            days: 'Days',
            hours: 'Hours',
            minutes: 'Minutes',
            seconds: 'Seconds'
          },
          mediaProps: {
            src: 'https://yjpipxmxaqnzbxyynuit.supabase.co/storage/v1/object/public/EventInvites/GreekVitesExample/Coffee.png',
            className: 'countdown-background',
            alt: 'Countdown background image',
            heightPercent: 30,
            shiftPercent: 40
          }
        };
        case 'ThankYouSection':
          return {
            image: 'https://yjpipxmxaqnzbxyynuit.supabase.co/storage/v1/object/public/EventInvites/GreekVitesExample/closingAtAltar.png',
            thankYouText: [
              "Thank you for being part of our special day",
              "Your presence means the world to us"
            ]
          };
      default:
        return {};
    }
  };

  const applyToAllContexts = (updater) => {
    const newFunctions = { ...template.functions };
    Object.keys(newFunctions).forEach(lang => {
      newFunctions[lang] = updater(newFunctions[lang]);
    });
    return newFunctions;
  };

  const handleAddModule = (moduleType) => {
    if (!unbind) {
      const newFunctions = applyToAllContexts(arr => [
        ...arr,
        {
          name: moduleType,
          props: getDefaultProps(moduleType)
        }
      ]);
      onUpdate({
        ...template,
        functions: newFunctions
      });
    } else {
      const newFunctions = [
        ...functionsArray,
        {
          name: moduleType,
          props: getDefaultProps(moduleType)
        }
      ];
      onUpdate({
        ...template,
        functions: {
          ...template.functions,
          [currentLanguage]: newFunctions
        }
      });
    }
    setIsAddModuleExpanded(false);
  };

  const handleDeleteComponent = (indexToDelete) => {
    if (!unbind) {
      const newFunctions = applyToAllContexts(arr => arr.filter((_, index) => index !== indexToDelete));
      onUpdate({
        ...template,
        functions: newFunctions
      });
    } else {
      const newFunctions = functionsArray.filter((_, index) => index !== indexToDelete);
      onUpdate({
        ...template,
        functions: {
          ...template.functions,
          [currentLanguage]: newFunctions
        }
      });
    }
  };

  if (!template || !template.functions) {
    return null;
  }

  const toggleComponent = (index) => {
    setExpandedComponents(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Format value for display and editing
  const formatValue = (value, isEditing = false) => {
    if (value === null || value === undefined) {
      return isEditing ? "" : <span className="null-value">null</span>;
    }

    if (typeof value === 'object') {
      if (Array.isArray(value)) {
        return isEditing ? JSON.stringify(value) : <span className="array-value">[Array:{value.length}]</span>;
      }
      
      // Object
      return isEditing ? JSON.stringify(value) : <span className="object-value">{`{Object:${Object.keys(value).length} keys}`}</span>;
    }

    if (typeof value === 'string') {
      return isEditing ? value : <span className="string-value">"{value}"</span>;
    }

    if (typeof value === 'boolean') {
      return isEditing ? value.toString() : <span className="boolean-value">{value.toString()}</span>;
    }

    return String(value);
  };

  const handlePropChange = (funcIndex, propKey, value) => {
    try {
      let parsedValue = value;
      if (value.startsWith('[') || value.startsWith('{')) {
        parsedValue = JSON.parse(value);
      } else if (value === 'true') {
        parsedValue = true;
      } else if (value === 'false') {
        parsedValue = false;
      } else if (!isNaN(value) && value !== '') {
        parsedValue = Number(value);
      }
      // Always update only the current language context for prop changes
      const newFunctions = functionsArray.map((func, idx) => {
        if (idx === funcIndex) {
          return {
            ...func,
            props: {
              ...func.props,
              [propKey]: parsedValue
            }
          };
        }
        return func;
      });
      onUpdate({
        ...template,
        functions: {
          ...template.functions,
          [currentLanguage]: newFunctions
        }
      });
    } catch (error) {
      console.error('Error updating prop:', error);
    }
  };

  const handleCustomUpdate = (funcIndex, newProps) => {
    console.log('ComponentInspector - handleCustomUpdate called with:', { funcIndex, newProps });
    console.log('ComponentInspector - Current function:', functionsArray[funcIndex]);
    
    // Always update only the current language context for custom updates
    const newFunctions = functionsArray.map((func, idx) => {
      if (idx === funcIndex) {
        const updatedFunc = {
          ...func,
          props: newProps
        };
        console.log('ComponentInspector - Updated function:', updatedFunc);
        return updatedFunc;
      }
      return func;
    });
    
    const updatedTemplate = {
      ...template,
      functions: {
        ...template.functions,
        [currentLanguage]: newFunctions
      }
    };
    
    console.log('ComponentInspector - Final template update:', updatedTemplate);
    onUpdate(updatedTemplate);
  };

  const renderDefaultInspector = (func, index, iconPickerProps) => (
    Object.entries(func.props || {}).length === 0 ? (
      <div className="no-props">No props</div>
    ) : (
      Object.entries(func.props || {}).map(([key, value]) => (
        <div key={key} className="prop-item">
          <span className="prop-name">{key}</span>
          <div className="prop-value-container">
            <textarea
              className="prop-editor"
              value={formatValue(value, true)}
              onChange={(e) => handlePropChange(index, key, e.target.value)}
              rows={value && typeof value === 'object' ? 4 : 1}
            />
          </div>
        </div>
      ))
    )
  );

  const handleAddModuleClick = () => {
    setIsAddModuleExpanded(!isAddModuleExpanded);
    // If we're opening the module picker, scroll to it
    if (!isAddModuleExpanded) {
      // Use a longer delay to ensure the module picker is fully expanded
      setTimeout(() => {
        if (inspectorContentRef.current && modulePickerRef.current) {
          inspectorContentRef.current.scrollTo({
            top: inspectorContentRef.current.scrollHeight,
            behavior: 'smooth'
          });
        }
      }, 300); // Longer delay to account for expansion animation
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = parseInt(active.id.split('-')[1]);
      const newIndex = parseInt(over.id.split('-')[1]);
      if (!unbind) {
        const newFunctions = applyToAllContexts(arr => arrayMove(arr, oldIndex, newIndex));
        onUpdate({
          ...template,
          functions: newFunctions
        });
      } else {
        const newFunctions = arrayMove(functionsArray, oldIndex, newIndex);
        onUpdate({
          ...template,
          functions: {
            ...template.functions,
            [currentLanguage]: newFunctions
          }
        });
      }
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleLanguageButtonClick = (e) => {
    setShowLanguageDropdown((prev) => !prev);
    setDropdownAnchor(e.currentTarget);
  };

  // eslint-disable-next-line no-unused-vars
  const handleSelectPrimaryLanguage = (langCode) => {
    const current = selectedLanguages || [];
    const newSelected = current.includes(langCode) ? current : [...current, langCode];
    onUpdate({
      ...template,
      primaryLanguage: langCode,
      selectedLanguages: newSelected
    });
    setShowLanguageDropdown(false);
  };

  // Handler to remove a language from the selected list
  const handleRemoveSelectedLanguage = (langCode) => {
    const newSelected = (selectedLanguages || []).filter(code => code !== langCode);
    let newPrimary = template.primaryLanguage;
    if (langCode === template.primaryLanguage) {
      newPrimary = newSelected.length > 0 ? newSelected[0] : null;
    }
    // If this is the last language being removed, rename its key to '__' instead of deleting
    if (selectedLanguages.length === 1 && selectedLanguages[0] === langCode) {
      const newFunctions = { ...template.functions };
      newFunctions['__'] = newFunctions[langCode];
      delete newFunctions[langCode];
      onUpdate({
        ...template,
        selectedLanguages: [],
        primaryLanguage: null,
        functions: newFunctions
      });
      if (typeof onLanguagesChange === 'function') onLanguagesChange([]);
      return;
    }
    onUpdate({
      ...template,
      selectedLanguages: newSelected,
      primaryLanguage: newPrimary
    });
    if (typeof onLanguagesChange === 'function') onLanguagesChange(newSelected);
  };

  // Handler to add a language to the selected list
  const handleAddSelectedLanguage = (langCode) => {
    const current = selectedLanguages || [];
    if (!current.includes(langCode)) {
      let newFunctions;
      const functionKeys = Object.keys(template.functions || {});
      if (functionKeys.length === 1 && functionKeys[0] === '__') {
        newFunctions = { [langCode]: template.functions['__'] };
        onUpdate({
          ...template,
          selectedLanguages: [langCode],
          functions: newFunctions
        });
        if (typeof setCurrentLang === 'function') setCurrentLang(langCode);
        if (typeof onLanguagesChange === 'function') onLanguagesChange([langCode]);
        return;
      }
      // Copy the current language's components as the initial value for the new language
      newFunctions = {
        ...template.functions,
        [langCode]: [...(template.functions[currentLang] || [])]
      };
      const newSelected = [...current, langCode];
      onUpdate({
        ...template,
        selectedLanguages: newSelected,
        functions: newFunctions
      });
      if (typeof onLanguagesChange === 'function') onLanguagesChange(newSelected);
    }
  };

  // Open the dropdown and calculate its position
  const openLanguageDropdown = () => {
    if (addLanguageBtnRef.current) {
      const rect = addLanguageBtnRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
    setLanguageSearch(''); // Clear the search field
    setShowLanguageDropdown(true);
  };

  const handleFlagClick = () => {
    if (flagBtnRef.current) {
      const rect = flagBtnRef.current.getBoundingClientRect();
      setFlagDropdownPos({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width
      });
      setShowFlagDropdown(true);
    }
  };

  const handleFlagDropdownSelect = (code) => {
    const current = selectedLanguages || [];
    const newSelected = [code, ...current.filter(c => c !== code)];
    onUpdate({
      ...template,
      selectedLanguages: newSelected
    });
    setShowFlagDropdown(false);
  };

  // Get the current language array
  const langKeys = template.functions ? Object.keys(template.functions) : [];
  const currentLanguage =
    currentLang ||
    (langKeys.length === 1 && langKeys[0] === '__' ? '__' : (langKeys.length > 0 ? langKeys[0] : null));
  const functionsArray = Array.isArray(template.functions?.[currentLanguage]) ? template.functions[currentLanguage] : [];

  return (
    <div 
      className={`component-inspector ${!isVisible ? 'hidden' : ''}`}
      style={{ zIndex: modalZIndices.properties }}
      onMouseDown={() => bringToFront('properties')}>
      <div className="inspector-header">
        <div className="header-content">
          <h2>Components View</h2>
          <p className="inspector-subtitle">{template.functions.length} components</p>
        </div>
        {/* Language flag/question mark in header */}
        {(multiLanguage && (selectedLanguages || []).length > 1) && (
          <span
            ref={flagBtnRef}
            className="inspector-flag-btn"
            onClick={handleFlagClick}
            tabIndex={0}
            title="Change primary language"
          >
            {(() => {
              const lang = curatedLanguages.find(l => l.code === currentLang);
              if (!lang) return null;
              return (
                <ReactCountryFlag
                  countryCode={lang.countryCode}
                  svg
                  style={{ width: 38, height: 28, objectFit: 'cover', borderRadius: 0, display: 'block' }}
                  title={lang.countryCode}
                />
              );
            })()}
          </span>
        )}
        {showFlagDropdown && ReactDOM.createPortal(
          <div
            className="flag-dropdown"
            style={{
              position: 'absolute',
              top: flagDropdownPos.top,
              left: flagDropdownPos.left,
              minWidth: flagDropdownPos.width || 40
            }}
            onClick={e => e.stopPropagation()}
            onMouseLeave={() => setShowFlagDropdown(false)}
          >
            {(selectedLanguages || []).map(code => {
              const lang = curatedLanguages.find(l => l.code === code);
              if (!lang) return null;
              return (
                <span
                  key={code}
                  className="flag-dropdown-flag"
                  onClick={() => {
                    handleFlagDropdownSelect(code);
                    if (setCurrentLang) {
                      setCurrentLang(code);
                    }
                  }}
                  title={lang.name}
                >
                  <ReactCountryFlag
                    countryCode={lang.countryCode}
                    svg
                    style={{ width: 38, height: 28, objectFit: 'cover', borderRadius: 0, display: 'block' }}
                    title={lang.countryCode}
                  />
                </span>
              );
            })}
          </div>,
          document.body
        )}
        <button 
          className="tool-icon-button"
          onClick={() => setIsPropertiesOpen(true)}
          title="Template Properties"
        >
          <svg 
            width="18" 
            height="18" 
            viewBox="0 0 24 24" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
          >
            <path 
              d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
            <path 
              d="M19.4 15C19.2669 15.3016 19.2272 15.6362 19.286 15.9606C19.3448 16.285 19.4995 16.5843 19.73 16.82L19.79 16.88C19.976 17.0657 20.1235 17.2863 20.2241 17.5291C20.3248 17.7719 20.3766 18.0322 20.3766 18.295C20.3766 18.5578 20.3248 18.8181 20.2241 19.0609C20.1235 19.3037 19.976 19.5243 19.79 19.71C19.6043 19.896 19.3837 20.0435 19.1409 20.1441C18.8981 20.2448 18.6378 20.2966 18.375 20.2966C18.1122 20.2966 17.8519 20.2448 17.6091 20.1441C17.3663 20.0435 17.1457 19.896 16.96 19.71L16.9 19.65C16.6643 19.4195 16.365 19.2648 16.0406 19.206C15.7162 19.1472 15.3816 19.1869 15.08 19.32C14.7842 19.4468 14.532 19.6572 14.3543 19.9255C14.1766 20.1938 14.0813 20.5082 14.08 20.83V21C14.08 21.5304 13.8693 22.0391 13.4942 22.4142C13.1191 22.7893 12.6104 23 12.08 23C11.5496 23 11.0409 22.7893 10.6658 22.4142C10.2907 22.0391 10.08 21.5304 10.08 21V20.91C10.0723 20.579 9.96512 20.258 9.77251 19.9887C9.5799 19.7194 9.31074 19.5143 9 19.4C8.69838 19.2669 8.36381 19.2272 8.03941 19.286C7.71502 19.3448 7.41568 19.4995 7.18 19.73L7.12 19.79C6.93425 19.976 6.71368 20.1235 6.47088 20.2241C6.22808 20.3248 5.96783 20.3766 5.705 20.3766C5.44217 20.3766 5.18192 20.3248 4.93912 20.2241C4.69632 20.1235 4.47575 19.976 4.29 19.79C4.10405 19.6043 3.95653 19.3837 3.85588 19.1409C3.75523 18.8981 3.70343 18.6378 3.70343 18.375C3.70343 18.1122 3.75523 17.8519 3.85588 17.6091C3.95653 17.3663 4.10405 17.1457 4.29 16.96L4.35 16.9C4.58054 16.6643 4.73519 16.365 4.794 16.0406C4.85282 15.7162 4.81312 15.3816 4.68 15.08C4.55324 14.7842 4.34276 14.532 4.07447 14.3543C3.80618 14.1766 3.49179 14.0813 3.17 14.08H3C2.46957 14.08 1.96086 13.8693 1.58579 13.4942C1.21071 13.1191 1 12.6104 1 12.08C1 11.5496 1.21071 11.0409 1.58579 10.6658C1.96086 10.2907 2.46957 10.08 3 10.08H3.09C3.42099 10.0723 3.742 9.96512 4.0113 9.77251C4.28059 9.5799 4.48572 9.31074 4.6 9C4.73312 8.69838 4.77282 8.36381 4.714 8.03941C4.65519 7.71502 4.50054 7.41568 4.27 7.18L4.21 7.12C4.02405 6.93425 3.87653 6.71368 3.77588 6.47088C3.67523 6.22808 3.62343 5.96783 3.62343 5.705C3.62343 5.44217 3.67523 5.18192 3.77588 4.93912C3.87653 4.69632 4.02405 4.47575 4.21 4.29C4.39575 4.10405 4.61632 3.95653 4.85912 3.85588C5.10192 3.75523 5.36217 3.70343 5.625 3.70343C5.88783 3.70343 6.14808 3.75523 6.39088 3.85588C6.63368 3.95653 6.85425 4.10405 7.04 4.29L7.1 4.35C7.33568 4.58054 7.63502 4.73519 7.95941 4.794C8.28381 4.85282 8.61838 4.81312 8.92 4.68H9C9.29577 4.55324 9.54802 4.34276 9.72569 4.07447C9.90337 3.80618 9.99872 3.49179 10 3.17V3C10 2.46957 10.2107 1.96086 10.5858 1.58579C10.9609 1.21071 11.4696 1 12 1C12.5304 1 13.0391 1.21071 13.4142 1.58579C13.7893 1.96086 14 2.46957 14 3V3.09C14.0013 3.41179 14.0966 3.72618 14.2743 3.99447C14.452 4.26276 14.7042 4.47324 15 4.6C15.3016 4.73312 15.6362 4.77282 15.9606 4.714C16.285 4.65519 16.5843 4.50054 16.82 4.27L16.88 4.21C17.0657 4.02405 17.2863 3.87653 17.5291 3.77588C17.7719 3.67523 18.0322 3.62343 18.295 3.62343C18.5578 3.62343 18.8181 3.67523 19.0609 3.77588C19.3037 3.87653 19.5243 4.02405 19.71 4.21C19.896 4.39575 20.0435 4.61632 20.1441 4.85912C20.2448 5.10192 20.2966 5.36217 20.2966 5.625C20.2966 5.88783 20.2448 6.14808 20.1441 6.39088C20.0435 6.63368 19.896 6.85425 19.71 7.04L19.65 7.1C19.4195 7.33568 19.2648 7.63502 19.206 7.95941C19.1472 8.28381 19.1869 8.61838 19.32 8.92V9C19.4468 9.29577 19.6572 9.54802 19.9255 9.72569C20.1938 9.90337 20.5082 9.99872 20.83 10H21C21.5304 10 22.0391 10.2107 22.4142 10.5858C22.7893 10.9609 23 11.4696 23 12C23 12.5304 22.7893 13.0391 22.4142 13.4142C22.0391 13.7893 21.5304 14 21 14H20.91C20.5882 14.0013 20.2738 14.0966 20.0055 14.2743C19.7372 14.452 19.5268 14.7042 19.4 15Z" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {isPropertiesOpen &&
        ReactDOM.createPortal(
          <div 
            className="properties-popover"
            style={{
              transform: `translate(${position.x}px, ${position.y}px)`,
            }}
            onMouseDown={handleMouseDown}
          >
            <div className="properties-header">Properties</div>
            <button 
              className="close-button"
              onClick={() => setIsPropertiesOpen(false)}
            >
              ×
            </button>
            <div className="theme-header">
              Theme Colors
              <button 
                className={`theme-collapse-button ${isCollapsed ? 'theme-collapsed' : ''}`}
                onClick={() => setIsCollapsed(!isCollapsed)}
              >
                <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            <div className={`theme-colors-content ${isCollapsed ? 'theme-collapsed' : ''}`}>
              {Object.entries(colors).map(([key, value]) => (
                <div key={key} className="color-row">
                  <input
                    type="color"
                    value={value}
                    onChange={(e) => handleColorsUpdate({
                      ...colors,
                      [key]: e.target.value
                    })}
                  />
                  <input
                    type="text"
                    value={value}
                    onChange={(e) => handleColorsUpdate({
                      ...colors,
                      [key]: e.target.value
                    })}
                  />
                  <span>({key})</span>
                </div>
              ))}
            </div>
            {/* --- Languages Section --- */}
            <div className="theme-header">
              Languages
              <button
                className={`theme-collapse-button ${isLanguagesCollapsed ? 'theme-collapsed' : ''}`}
                onClick={() => setIsLanguagesCollapsed(!isLanguagesCollapsed)}
              >
                <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            <div className={`theme-colors-content ${isLanguagesCollapsed ? 'theme-collapsed' : ''}`}>
              <div className="languages-toggle-row">
                <label>Enable Multi-Language Support</label>
                <input
                  type="checkbox"
                  checked={multiLanguage}
                  onChange={onMultiLanguageToggle}
                />
              </div>
              {/* Selected languages as labels/chips + Add Language chip */}
              {multiLanguage && (
                <div className="language-chips-row">
                  {(selectedLanguages && selectedLanguages.length > 0
                    ? selectedLanguages
                    : (Object.keys(template.functions).includes('__') ? ['__'] : [])
                  ).map(code => {
                    if (code === '__') {
                      return (
                        <span key={code} className="language-chip">
                          <span className="language-chip-flag">
                            <span style={{ width: 18, height: 14, display: 'inline-block', background: '#eee', borderRadius: 2, marginRight: 4, textAlign: 'center', fontWeight: 600, fontSize: 12, color: '#888' }}>DF</span>
                          </span>
                          Default
                        </span>
                      );
                    }
                    const lang = curatedLanguages.find(l => l.code === code);
                    if (!lang) return null;
                    return (
                      <span key={code} className="language-chip">
                        <span className="language-chip-flag">
                          <ReactCountryFlag
                            countryCode={lang.countryCode}
                            svg
                            style={{ width: 18, height: 14, objectFit: 'cover', borderRadius: 0 }}
                            title={lang.countryCode}
                          />
                        </span>
                        {lang.name}
                        <button
                          className="language-chip-remove"
                          onClick={() => handleRemoveSelectedLanguage(code)}
                          title={`Remove ${lang.name}`}
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                  {/* Add Language chip */}
                  <span
                    ref={addLanguageBtnRef}
                    className="add-language-chip"
                    onClick={openLanguageDropdown}
                    tabIndex={0}
                  >
                    <span className="add-language-chip-plus">+</span> Add Language
                  </span>
                </div>
              )}
              <div className="languages-toggle-row">
                <label style={{ color: multiLanguage ? undefined : '#aaa' }}>Unbind language templates</label>
                <input
                  type="checkbox"
                  checked={unbind}
                  onChange={handleUnbindLanguageTemplatesToggle}
                  disabled={
                    // Disable if not enough languages, or if forced true due to mismatch
                    !(multiLanguage && (selectedLanguages || []).length > 1) ||
                    (!allContextsEqual && (selectedLanguages || []).length > 1)
                  }
                />
              </div>
            </div>
          </div>,
          document.body
        )
      }

      <div className="inspector-content" ref={inspectorContentRef}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={functionsArray.map((_, index) => `sortable-${index}`)}
            strategy={verticalListSortingStrategy}
          >
            {functionsArray.map((func, index) => (
              <SortableComponent
                key={`sortable-${index}`}
                func={func}
                index={index}
                expandedComponents={expandedComponents}
                onToggle={toggleComponent}
                onDelete={handleDeleteComponent}
                CustomInspector={getInspector(func.name)}
                handleCustomUpdate={handleCustomUpdate}
                renderDefaultInspector={renderDefaultInspector}
                isIconPickerOpen={isIconPickerOpen}
                setIsIconPickerOpen={setIsIconPickerOpen}
                iconPickerPosition={iconPickerPosition}
                setIconPickerPosition={setIconPickerPosition}
                setActiveIconPreview={setActiveIconPreview}
                isPhotoPickerOpen={isPhotoPickerOpen}
                setIsPhotoPickerOpen={setIsPhotoPickerOpen}
                photoPickerPosition={photoPickerPosition}
                setPhotoPickerPosition={setPhotoPickerPosition}
                setActivePhotoPreview={setActivePhotoPreview}
                bringToFront={bringToFront}
              />
            ))}
          </SortableContext>
        </DndContext>
        
        {/* Add Module Section */}
        <div 
          ref={modulePickerRef}
          className={`add-module-box ${isAddModuleExpanded ? 'expanded' : ''}`}
        >
          <div 
            className="add-module-button"
            onClick={handleAddModuleClick}
          >
            <svg 
              width="20" 
              height="20" 
              viewBox="0 0 20 20" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
            >
              <path 
                d="M10 4V16M4 10H16" 
                stroke="white" 
                strokeWidth="2" 
                strokeLinecap="round" 
              />
            </svg>
            <span>Add Module</span>
          </div>
          
          {isAddModuleExpanded && (
            <div className="module-picker">
              <h4>Select a module to add:</h4>
              <div className="module-list">
                {availableModules.map((moduleType) => (
                  <button
                    key={moduleType}
                    className="module-option"
                    onClick={() => handleAddModule(moduleType)}
                  >
                    {formatComponentName(moduleType)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Language dropdown prompt as a portal tooltip */}
      {showLanguageDropdown && ReactDOM.createPortal(
        <div
          ref={languageDropdownRef}
          className="language-dropdown-portal custom-language-dropdown"
          style={{
            position: 'absolute',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: dropdownPosition.width || 200
          }}
        >
          <button
            className="language-dropdown-clear-btn"
            onClick={() => setLanguageSearch('')}
            title="Clear search"
          >
            ×
          </button>
          <input
            type="text"
            className="language-dropdown-search language-search-input"
            placeholder="Search"
            value={languageSearch}
            onChange={e => setLanguageSearch(e.target.value)}
            autoFocus
          />
          <style>{`
            .custom-language-dropdown::-webkit-scrollbar { display: none; }
            .custom-language-dropdown { scrollbar-width: none; -ms-overflow-style: none; }
          `}</style>
          <div className="language-dropdown-list">
            {filteredLanguages.map(l => {
              const countryName = new Intl.DisplayNames(['en'], { type: 'region' }).of(l.countryCode);
              return (
                <div
                  key={l.code}
                  className="language-dropdown-item"
                  onClick={() => { handleAddSelectedLanguage(l.code); setShowLanguageDropdown(false); }}
                >
                  <span className="language-dropdown-flag">
                    <ReactCountryFlag
                      countryCode={l.countryCode}
                      svg
                      style={{ width: 28, height: 20, objectFit: 'cover', borderRadius: 0 }}
                      title={l.countryCode}
                    />
                  </span>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                    <span className="language-dropdown-label">{l.name}</span>
                    <span className="language-dropdown-country">{countryName}</span>
                  </div>
                </div>
              );
            })}
            {filteredLanguages.length === 0 && (
              <div className="language-dropdown-no-results">No results</div>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Shared IconPicker */}
      <IconPicker
        isOpen={isIconPickerOpen}
        onClose={() => {
          console.log('Closing icon picker, current position:', iconPickerPosition);
          setIsIconPickerOpen(false);
        }}
        position={iconPickerPosition || { x: 0, y: 0 }}
        zIndex={modalZIndices.iconPicker}
        onFocus={() => bringToFront('iconPicker')}
        currentIcon={activeIconPreview ? (() => {
          // If it's a number, it's a location index
          if (!isNaN(activeIconPreview.index)) {
            const locationIndex = activeIconPreview.index;
            return functionsArray[0]?.props?.locations?.[locationIndex]?.icon;
          }
          // Otherwise it's a section index
          const [parentIndex, section] = activeIconPreview.index.toString().split('-');
          const parentProps = functionsArray[parentIndex]?.props;
          if (section === 'top') {
            return parentProps?.topMessageProps?.icon;
          } else if (section === 'bottom') {
            return parentProps?.bottomMessageProps?.icon;
          } else {
            return parentProps?.icon;
          }
        })() : null}
        onDragEnd={(newPosition) => {
          console.log('Modal dragged to:', newPosition);
          setIconPickerPosition(newPosition);
        }}
        onSelect={(icon) => {
          if (activeIconPreview) {
            console.log('Received icon in ComponentInspector:', icon);
            
            console.log('Active icon preview:', activeIconPreview);
            console.log('Selected icon:', icon);
            
            // Parse the index to determine what we're updating
            let funcIndex;
            let currentFunc;
            let updateType = 'regular'; // Can be 'regular', 'location', or 'dress'
            let locationIndex;
            
            // Safety check for icon object
            if (!icon || !icon.svg_path) {
              console.error('Invalid icon object received:', icon);
              return;
            }
            
            if (typeof activeIconPreview.index === 'string' && activeIconPreview.index.startsWith('dress-')) {
              // It's a dress code SVG
              funcIndex = parseInt(activeIconPreview.index.split('-')[1]);
              updateType = 'dress';
            } else if (typeof activeIconPreview.index === 'string' && activeIconPreview.index.includes('-')) {
              const [parentIdx, section] = activeIconPreview.index.toString().split('-');
              funcIndex = parseInt(parentIdx);
              
              if (section === 'top') {
                // It's a top message section icon
                updateType = 'dressCodeTop';
              } else if (section === 'message') {
                // It's an attendance questions message section icon
                updateType = 'attendanceMessage';
              } else {
                // It's a location icon
                locationIndex = parseInt(section);
                updateType = 'location';
              }
            } else {
              // It's a regular component icon
              funcIndex = parseInt(activeIconPreview.index);
            }

            // Safety check for function index
            if (isNaN(funcIndex) || funcIndex < 0 || funcIndex >= functionsArray.length) {
              console.error('Invalid function index:', funcIndex);
              return;
            }

            currentFunc = functionsArray[funcIndex];
            if (!currentFunc) {
              console.error('Could not find function at index:', funcIndex);
              return;
            }

            // Get the latest props from the template state
            const latestProps = template.functions[currentLanguage][funcIndex].props;
            if (!latestProps) {
              console.error('Could not find props for function:', currentFunc);
              return;
            }
            
            console.log('Latest props before update:', latestProps);
            let updatedProps;
            
            switch (updateType) {
              case 'attendanceMessage':
                // Handle attendance questions message section icon update
                updatedProps = {
                  ...latestProps,
                  messageProps: {
                    ...(latestProps.messageProps || {}),
                    icon: {
                      ...(latestProps.messageProps?.icon || {}),
                      viewBox: icon.viewBox || "0 0 24 24",
                      svg_path: icon.svg_path,
                      // Preserve these properties if they exist
                      fill: latestProps.messageProps?.icon?.fill || "false",
                      useHighlight: latestProps.messageProps?.icon?.useHighlight || "false"
                    }
                  }
                };
                break;

              case 'dressCodeTop':
                // Handle top message section icon update
                updatedProps = {
                  ...latestProps,
                  topMessageProps: {
                    ...(latestProps.topMessageProps || {}),
                    icon: {
                      ...(latestProps.topMessageProps?.icon || {}),
                      viewBox: icon.viewBox || "0 0 24 24",
                      svg_path: icon.svg_path,
                      // Preserve these properties if they exist
                      fill: latestProps.topMessageProps?.icon?.fill || "false",
                      useHighlight: latestProps.topMessageProps?.icon?.useHighlight || "false"
                    }
                  }
                };
                break;
                
              case 'dress':
                // Handle dress code SVG update
                updatedProps = {
                  ...latestProps,
                  colorProp: {
                    ...(latestProps.colorProp || {}),
                    svg: {
                      ...(latestProps.colorProp?.svg || {}),
                      d: icon.svg_path,
                      viewBox: "0 0 24 24"
                    }
                  }
                };
                break;
                
              case 'location':
                // Handle location icon update
                if (locationIndex === undefined || !Array.isArray(latestProps.locations)) {
                  console.error('Invalid location index or locations array:', { locationIndex, locations: latestProps.locations });
                  return;
                }
                
                const locations = [...latestProps.locations];
                const location = locations[locationIndex];
                if (!location) {
                  console.error('Could not find location at index:', locationIndex);
                  return;
                }
                
                locations[locationIndex] = {
                  ...location,
                  icon: {
                    ...(location.icon || {}),
                    viewBox: "0 0 24 24",
                    svg_path: icon.svg_path
                  }
                };
                updatedProps = {
                  ...latestProps,
                  locations: locations
                };
                break;
                
              default:
                // Handle regular component icon update
                const currentIcon = latestProps.icon || {};
                const newIcon = {
                  ...currentIcon,
                  viewBox: icon.viewBox || "0 0 24 24",
                  svg_path: icon.svg_path,
                  // Explicitly preserve these properties
                  fill: currentIcon.fill || "false",
                  useHighlight: currentIcon.useHighlight || "false"
                };
                updatedProps = {
                  ...latestProps,
                  icon: newIcon
                };
            }
            
            // Final safety check
            if (!updatedProps) {
              console.error('Failed to create updated props');
              return;
            }
            
            // Update using handleCustomUpdate to ensure consistent update path
            handleCustomUpdate(funcIndex, updatedProps);
          }
        }}
      />

      {/* Shared   */}
      <PhotoPicker
        isOpen={isPhotoPickerOpen}
        onClose={() => {
          console.log('Closing photo picker, current position:', photoPickerPosition);
          setIsPhotoPickerOpen(false);
        }}
        position={photoPickerPosition || { x: 0, y: 0 }}
        zIndex={modalZIndices.photoPicker}
        onFocus={() => bringToFront('photoPicker')}
        currentPhoto={activePhotoPreview ? functionsArray[activePhotoPreview.index]?.props?.src : null}
        onDragEnd={(newPosition) => {
          console.log('Modal dragged to:', newPosition);
          setPhotoPickerPosition(newPosition);
        }}
        templateId={id}
        onSelect={(photoUrl) => {
          if (activePhotoPreview) {
            const currentFunc = functionsArray[activePhotoPreview.index];
            
            console.log("-----------------------------------------");
            console.log('Selected photo URL:', photoUrl);
            console.log('Current Function:', currentFunc);

            if (currentFunc) {
              // Get the current function and its latest props
              const latestProps = template.functions[currentLanguage][activePhotoPreview.index].props;
              console.log('Latest props before update:', latestProps);
              
              // Determine which prop to update based on the component type
              let updatedProps;
              
              switch(currentFunc.name) {
                case 'WelcomeImage':
                  updatedProps = {
                    ...latestProps,
                    backgroundImage: photoUrl
                  };
                  break;
                  
                case 'ThankYouSection':
                  updatedProps = {
                    ...latestProps,
                    image: photoUrl
                  };
                  break;
                  
                case 'CountdownContainer':
                  updatedProps = {
                    ...latestProps,
                    mediaProps: {
                      ...(latestProps.mediaProps || {}),
                      src: photoUrl
                    }
                  };
                  break;
                  
                default: // MediaAsset and other components
                  updatedProps = {
                    ...latestProps,
                    src: photoUrl
                  };
              }
              
              // Update using handleCustomUpdate to ensure consistent update path
              handleCustomUpdate(activePhotoPreview.index, updatedProps);
            }

            console.log("-----------------------------------------");
          }
        }}
      />
    </div>
  );
};

export default ComponentInspector; 